const OFFLINE_BANNER_ID = "wmu-offline-banner";
const OFFLINE_VISIBLE_CLASS = "is-visible";
const OFFLINE_HTML_CLASS = "is-offline";
const OFFLINE_DEBOUNCE_MS = 2000;

export type WMUOfflineAPI = {
  boot: () => void;
  show: () => void;
  hide: () => void;
  setVisible: (visible: boolean) => void;
  isOffline: () => boolean;
};

let showTimer = 0;
let offlineVisible = false;

function ensureBanner(): HTMLElement {
  const existing = document.getElementById(OFFLINE_BANNER_ID);
  if (existing instanceof HTMLElement) {
    return existing;
  }
  const banner = document.createElement("div");
  banner.id = OFFLINE_BANNER_ID;
  banner.className = "wm-offline-banner";
  banner.setAttribute("role", "status");
  banner.setAttribute("aria-live", "assertive");
  banner.setAttribute("aria-atomic", "true");
  banner.textContent = "You're offline — actions will be saved and synced when connection returns.";
  document.body?.prepend(banner);
  return banner;
}

function setVisible(visible: boolean): void {
  const banner = ensureBanner();
  banner.classList.toggle(OFFLINE_VISIBLE_CLASS, visible);
  banner.setAttribute("aria-hidden", visible ? "false" : "true");
  document.documentElement.classList.toggle(OFFLINE_HTML_CLASS, visible);
  offlineVisible = visible;
  window.dispatchEvent(new CustomEvent("wmu:offline-state", { detail: { offline: visible } }));
}

function clearShowTimer(): void {
  if (!showTimer) {
    return;
  }
  window.clearTimeout(showTimer);
  showTimer = 0;
}

function show(): void {
  clearShowTimer();
  setVisible(true);
}

function hide(): void {
  clearShowTimer();
  setVisible(false);
}

function scheduleOfflineBanner(): void {
  clearShowTimer();
  showTimer = window.setTimeout(() => {
    showTimer = 0;
    if (!navigator.onLine) {
      setVisible(true);
    }
  }, OFFLINE_DEBOUNCE_MS);
}

function bindHTMXOfflineFeedback(): void {
  const root = document.body;
  if (!(root instanceof HTMLElement) || root.dataset.offlineFeedbackBound === "true") {
    return;
  }
  root.dataset.offlineFeedbackBound = "true";

  const warnOffline = () => {
    if (navigator.onLine) {
      return;
    }
    setVisible(true);
    window.WMUToast?.warning?.("You're offline. This action will sync when the connection returns.");
  };

  root.addEventListener("htmx:sendError", warnOffline);
  root.addEventListener("htmx:responseError", warnOffline);
}

export function boot(): void {
  if (window.__WMU_OFFLINE_BOOTED) {
    return;
  }
  window.__WMU_OFFLINE_BOOTED = true;

  if (!navigator.onLine) {
    scheduleOfflineBanner();
  } else {
    hide();
  }

  window.addEventListener("offline", scheduleOfflineBanner, { passive: true });
  window.addEventListener("online", hide, { passive: true });
  bindHTMXOfflineFeedback();
}

window.WMUOffline = {
  boot,
  show,
  hide,
  setVisible,
  isOffline: () => offlineVisible,
} satisfies WMUOfflineAPI;

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
